import { TourStep } from "./types";

/** String = exact pathname match, RegExp = `.test(pathname)`. */
export const matchesRoute = (
  triggerRoute: string | RegExp,
  pathname: string,
): boolean =>
  typeof triggerRoute === "string"
    ? triggerRoute === pathname
    : triggerRoute.test(pathname);

/**
 * Rendered and not `display: none`. An element inside a closed `<dialog>` is
 * in the DOM but has no client rects, which is exactly the case `waitFor`
 * has to see through (UploadModal is always mounted, only opened on demand).
 */
// A closed DaisyUI `<dialog class="modal">` keeps its layout (display:grid,
// opacity:0) instead of the UA's display:none, so a box alone is not proof
// the officer can see the element.
export const isVisible = (el: Element | null | undefined): el is Element =>
  !!el && el.getClientRects().length > 0 && !el.closest("dialog:not([open])");

/** The selector a step waits on: `waitFor` when set, else its string target. */
export const stepWaitSelector = (step: TourStep | undefined): string | null => {
  if (!step) return null;
  if (step.waitFor) return step.waitFor;
  return typeof step.target === "string" ? step.target : null;
};

export interface Waiter {
  promise: Promise<Element | null>;
  cancel: () => void;
}

/**
 * Poll until `selector` is present and visible, or `timeoutMs` elapses
 * (resolves null). `cancel()` settles nothing: the caller just stops caring.
 */
export const waitForVisible = (
  selector: string,
  timeoutMs: number,
  intervalMs = 200,
): Waiter => {
  let cancelled = false;
  let timer: number | null = null;
  const promise = new Promise<Element | null>((resolve) => {
    const deadline = Date.now() + timeoutMs;
    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(selector);
      if (isVisible(el)) {
        resolve(el);
      } else if (Date.now() < deadline) {
        timer = window.setTimeout(tick, intervalMs);
      } else {
        resolve(null);
      }
    };
    tick();
  });
  return {
    promise,
    cancel: () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    },
  };
};

/**
 * A `<dialog>` opened with `showModal()` sits in the browser's top layer,
 * above anything z-index can reach, so a tooltip portaled to `<body>` would
 * be hidden behind it. Steps whose target is inside such a dialog have the
 * joyride portals moved into the dialog itself.
 */
export const dialogHostFor = (el: Element | null | undefined): HTMLElement | null =>
  (el?.closest("dialog[open]") as HTMLElement | null) ?? null;

export const JOYRIDE_PORTAL_ID = "react-joyride-portal";
/** react-floater's portal node for step `index` (id set by joyride). */
export const floaterPortalId = (index: number) => `react-joyride-step-${index}`;

const tourPortalNodes = (): HTMLElement[] =>
  Array.from(
    document.querySelectorAll<HTMLElement>(
      `#${JOYRIDE_PORTAL_ID}, [id^="react-joyride-step-"]`,
    ),
  );

/**
 * Move the overlay portal and the current step's tooltip portal into `host`
 * (`document.body` to restore). Empty tooltip portals left behind by earlier
 * steps are removed: joyride/react-floater only clean up nodes still under
 * `<body>`.
 */
export const relocateTourPortals = (host: HTMLElement, stepIndex: number) => {
  const wanted = new Set([JOYRIDE_PORTAL_ID, floaterPortalId(stepIndex)]);
  const body = document.body;
  for (const node of tourPortalNodes()) {
    if (wanted.has(node.id)) {
      if (node.parentElement !== host) host.appendChild(node);
      // DaisyUI's `.modal` has `pointer-events: none` (its box opts back in),
      // which the portal would inherit.
      node.style.pointerEvents = host === body ? "" : "auto";
    } else if (node.parentElement !== body && !node.hasChildNodes()) {
      node.remove();
    }
  }
};

/** Put every tour portal back under `<body>` (or drop it if already empty). */
export const restoreTourPortals = () => {
  const body = document.body;
  for (const node of tourPortalNodes()) {
    if (node.parentElement === body) continue;
    if (node.hasChildNodes()) {
      body.appendChild(node);
      node.style.pointerEvents = "";
    } else {
      node.remove();
    }
  }
};

/**
 * Query parameter that starts a tour as soon as the app loads
 * (`/cases?tour=first-narrative-report`). The Academy's "Start the
 * walkthrough" opens the app in a new tab with it; `TutorialProvider` reads
 * it once, starts the tour, and removes it from the URL.
 */
export const TOUR_QUERY_PARAM = "tour";

/** `search` with the tour parameter removed ("" when nothing is left). */
export function stripTourParam(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(TOUR_QUERY_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}
